import React from 'react';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { Box, Container, Typography, Paper, Button } from '@mui/material';
import { FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { setUser } from '../store/userSlice';
import { FormikTextField } from '../components/FormikTextField';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import {
  useLogin2faMutation,
  useWebauthnAuthOptionsMutation,
  useWebauthnAuthVerifyMutation,
  useWebauthnRegisterOptionsMutation,
  useWebauthnRegisterVerifyMutation,
} from '../services/authApi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  credToJSON,
  toPublicKeyCredentialCreationOptions,
  toPublicKeyCredentialRequestOptions,
} from '@/utils/webauthn';

const validationSchema = yup.object({
  token: yup
    .string()
    .matches(/^\d{6}$/g, 'Enter the 6-digit code')
    .required('Code is required'),
});

export default function Login2FA() {
  const { enqueueSnackbar } = useSnackbar();
  const [login2fa] = useLogin2faMutation();
  const [webauthnAuthOptions] = useWebauthnAuthOptionsMutation();
  const [webauthnAuthVerify] = useWebauthnAuthVerifyMutation();
  const [webauthnRegisterOptions] = useWebauthnRegisterOptionsMutation();
  const [webauthnRegisterVerify] = useWebauthnRegisterVerifyMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const from = (location.state?.from as string) || '/';
  const preAuthTokenFromState = location.state?.preAuthToken as string | undefined;
  const searchParams = new URLSearchParams(location.search);
  const preAuthTokenFromQuery = searchParams.get('pt') || undefined;
  const preAuthToken = preAuthTokenFromState || preAuthTokenFromQuery;
  const availableMfaMethods = (location.state?.availableMfaMethods as Array<'totp' | 'passkey'>) || [];
  const enrollableMfaMethods = (location.state?.enrollableMfaMethods as Array<'totp' | 'passkey'>) || [];
  const preferredMfaMethod = (location.state?.preferredMfaMethod as 'totp' | 'passkey' | undefined) || undefined;
  const [method, setMethod] = React.useState<'totp' | 'passkey'>(
    preferredMfaMethod && availableMfaMethods.includes(preferredMfaMethod)
      ? preferredMfaMethod
      : (availableMfaMethods[0] || 'totp')
  );
  const PASSKEYS_ENABLED = String(import.meta.env.VITE_PASSKEYS_ENABLED || 'true') !== 'false';
  const isPasskeySupported = PASSKEYS_ENABLED && typeof window !== 'undefined' && 'PublicKeyCredential' in window;
  const noAvailable = availableMfaMethods.length === 0;

  if (!preAuthToken) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Two-factor authentication token missing
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please log in again to continue.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Back to Login
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" gutterBottom>
            Complete two-factor authentication
          </Typography>
          {!noAvailable && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Choose a method:
              </Typography>
              <RadioGroup
                row
                name="mfa-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as 'totp' | 'passkey')}
              >
                {availableMfaMethods.includes('passkey') && isPasskeySupported && (
                  <FormControlLabel value="passkey" control={<Radio />} label="Passkey" />
                )}
                {availableMfaMethods.includes('totp') && (
                  <FormControlLabel value="totp" control={<Radio />} label="TOTP" />
                )}
              </RadioGroup>
            </Box>
          )}

          {!noAvailable && method === 'totp' ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Open your authenticator app and enter the 6-digit code.
              </Typography>
              <Formik
                initialValues={{ token: '' }}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                  try {
                    const response = await login2fa({ preAuthToken, token: values.token }).unwrap();
                    const userData = {
                      userId: response.userId || null,
                      name: response.name || null,
                      email: response.email || null,
                      role: response.role || null,
                      jwtToken: response.jwtToken || null,
                      amcIds: response.amcIds,
                      amcAccess: response.amcAccess,
                    };
                    dispatch(setUser(userData));
                    const to = from !== '/mfa/setup-prompt' ? from : '/dashboard';
                    navigate(to, { replace: true });
                  } catch (error: any) {
                    const errorMessage = error.data?.message || 'Invalid or expired code';
                    enqueueSnackbar(errorMessage, { variant: 'error' });
                  }
                }}
              >
                <Form style={{ width: '100%', marginTop: '8px' }}>
                  <FormikTextField
                    name="token"
                    margin="normal"
                    label="6-digit code"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                  />
                  <FormikSubmitButton fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                    Verify & Continue
                  </FormikSubmitButton>
                </Form>
              </Formik>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                {enrollableMfaMethods.includes('passkey') &&
                  !availableMfaMethods.includes('passkey') &&
                  isPasskeySupported && (
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        try {
                          const creationOptionsJson = await webauthnRegisterOptions({ preAuthToken }).unwrap();
                          const creationOptions = toPublicKeyCredentialCreationOptions(creationOptionsJson);
                          const cred: any = await navigator.credentials.create({ publicKey: creationOptions });
                          const serialized = credToJSON(cred);
                          const response = await webauthnRegisterVerify({
                            preAuthToken,
                            attestationResponse: serialized,
                          }).unwrap();
                          const userData = {
                            userId: response.userId || null,
                            name: response.name || null,
                            email: response.email || null,
                            role: response.role || null,
                            jwtToken: response.jwtToken || null,
                            amcIds: response.amcIds,
                            amcAccess: response.amcAccess,
                          };
                          dispatch(setUser(userData));
                          const to = from !== '/mfa/setup-prompt' ? from : '/dashboard';
                          navigate(to, { replace: true });
                        } catch (err: any) {
                          enqueueSnackbar('Passkey registration failed or was cancelled. You can continue with TOTP.', { variant: 'warning' });
                        }
                      }}
                    >
                      Register a Passkey
                    </Button>
                  )}
              </Stack>
            </>
          ) : !noAvailable && method === 'passkey' ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Use a passkey on this device to continue.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={async () => {
                  try {
                    // Try authentication first
                    const optionsJson = await webauthnAuthOptions({ preAuthToken }).unwrap();
                    const requestOptions = toPublicKeyCredentialRequestOptions(optionsJson);
                    const assertion: any = await navigator.credentials.get({ publicKey: requestOptions });
                    const serialized = credToJSON(assertion);
                    const response = await webauthnAuthVerify({
                      preAuthToken,
                      assertionResponse: serialized,
                    }).unwrap();
                    const userData = {
                      userId: response.userId || null,
                      name: response.name || null,
                      email: response.email || null,
                      role: response.role || null,
                      jwtToken: response.jwtToken || null,
                      amcIds: response.amcIds,
                      amcAccess: response.amcAccess,
                    };
                    dispatch(setUser(userData));
                    const to = from !== '/mfa/setup-prompt' ? from : '/dashboard';
                    navigate(to, { replace: true });
                  } catch (errAuth: any) {
                    // If auth fails, attempt registration (new device)
                    try {
                      const creationOptionsJson = await webauthnRegisterOptions({ preAuthToken }).unwrap();
                      const creationOptions = toPublicKeyCredentialCreationOptions(creationOptionsJson);
                      const cred: any = await navigator.credentials.create({ publicKey: creationOptions });
                      const serialized = credToJSON(cred);
                      const response = await webauthnRegisterVerify({
                        preAuthToken,
                        attestationResponse: serialized,
                      }).unwrap();
                      const userData = {
                        userId: response.userId || null,
                        name: response.name || null,
                        email: response.email || null,
                        role: response.role || null,
                        jwtToken: response.jwtToken || null,
                        amcIds: response.amcIds,
                        amcAccess: response.amcAccess,
                      };
                      dispatch(setUser(userData));
                      const to = from !== '/mfa/setup-prompt' ? from : '/dashboard';
                      navigate(to, { replace: true });
                    } catch (errReg: any) {
                      enqueueSnackbar(
                        'Passkey not available or operation was cancelled. You can use TOTP instead.',
                        { variant: 'warning' }
                      );
                    }
                  }
                }}
              >
                Continue with Passkey
              </Button>
              {/* Removed 'Use TOTP instead' toggle per requirement */}
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                No verification methods are currently available. You can set up a method to continue.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {enrollableMfaMethods.includes('totp') && (
                  <Button
                    variant="contained"
                    onClick={() => navigate('/mfa/setup', { state: { from, preAuthToken }, replace: true })}
                  >
                    Set up TOTP
                  </Button>
                )}
                {enrollableMfaMethods.includes('passkey') && isPasskeySupported && (
                  <Button
                    variant="contained"
                    onClick={async () => {
                      try {
                        const creationOptionsJson = await webauthnRegisterOptions({ preAuthToken }).unwrap();
                        const creationOptions = toPublicKeyCredentialCreationOptions(creationOptionsJson);
                        const cred: any = await navigator.credentials.create({ publicKey: creationOptions });
                        const serialized = credToJSON(cred);
                        const response = await webauthnRegisterVerify({
                          preAuthToken,
                          attestationResponse: serialized,
                        }).unwrap();
                        const userData = {
                          userId: response.userId || null,
                          name: response.name || null,
                          email: response.email || null,
                          role: response.role || null,
                          jwtToken: response.jwtToken || null,
                          amcIds: response.amcIds,
                          amcAccess: response.amcAccess,
                        };
                        dispatch(setUser(userData));
                        const to = from !== '/mfa/setup-prompt' ? from : '/dashboard';
                        navigate(to, { replace: true });
                      } catch (err: any) {
                        enqueueSnackbar('Passkey registration failed or was cancelled. Please try TOTP instead.', { variant: 'warning' });
                      }
                    }}
                  >
                    Register a Passkey
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
